import { useMutation, useQuery } from "@apollo/client";
import { isEqual, pick } from "lodash";
import moment from "moment";
import { useCallback, useMemo, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import {
  AssessmentGroupListFragmentFragment,
  ComplianceProgramAppliesTo,
  ComplianceProgramCalibrationType,
  ComplianceProgramRecurrence,
  ComplianceProgramState,
  ComplianceProgramStep,
  GetAssessmentGroupsListQuery,
  GetAssessmentGroupsListQueryVariables,
  GetComplianceProgramEditQuery,
  GetComplianceProgramEditQueryVariables,
  SaveComplianceProgramMutation,
  SaveComplianceProgramMutationVariables,
} from "types/graphql-schema";
import { FormError } from "types/topicflow";

import getAssessmentGroupsListQuery from "@apps/assessments/graphql/get-assessment-groups-list-query";
import createOrUpdateComplianceProgramMutation from "@apps/programs/graphql/create-or-update-compliance-program-mutation";
import getComplianceProgramEditQuery from "@apps/programs/graphql/get-compliance-program-edit-query";
import getComplianceProgramsQuery from "@apps/programs/graphql/get-compliance-programs-query";
import { NO_PARTICIPANTS_ERROR } from "@apps/programs/helpers";
import { useProgramsSection } from "@apps/programs/programs-section-context";
import useLabel from "@apps/use-label/use-label";
import {
  currentOrganizationVar,
  currentUserVar,
  isAdminVar,
  successNotificationVar,
} from "@cache/cache";
import Button from "@components/button/button";
import { ButtonSize, buttonTheme } from "@components/button/button-constants";
import Callout from "@components/callout/callout";
import Layout from "@components/layout/layout";
import { useLink } from "@components/link/link-helpers";
import Loading from "@components/loading/loading";
import Tabs from "@components/tabs/tabs";
import { assertEdgesNonNull } from "@helpers/helpers";
import useConfirm from "@helpers/hooks/use-confirm";
import useUrlQueryParams from "@helpers/hooks/use-url-query-params";

import {
  ProgramSaveMode,
  convertComplianceProgramToForm,
  convertProgramFormToMutationVariables,
  getDefaultProgramDates,
  getDefaultProgramFormStep,
  isKickoffDatePast,
  validateProgramForm,
} from "./form-data";
import ProgramFormNotifications from "./form-tabs/program-form-notifications";
import ProgramFormParticipants from "./form-tabs/program-form-participants";
import ProgramFormQuestions from "./form-tabs/program-form-questions";
import ProgramFormReadiness from "./form-tabs/program-form-readiness";
import ProgramFormTimelinePreview from "./form-tabs/program-form-timeline-preview";
import ProgramFormWorkflow from "./form-tabs/program-form-workflow";
import ProgramFormErrorBanner from "./program-form-error-banner";
import {
  ComplianceProgramType,
  ProgramEvaluationPeriodType,
  ProgramForm,
  ProgramFormStep,
  ProgramFormTab,
} from "./types";

const fieldToTab: Record<string, ProgramFormTab> = {
  title: ProgramFormTab.Workflow,
  startDate: ProgramFormTab.Workflow,
  dueDate: ProgramFormTab.Workflow,
  startDateOffset: ProgramFormTab.Workflow,
  startDateDueDateOffset: ProgramFormTab.Workflow,
  startDatePeriodStartOffset: ProgramFormTab.Workflow,
  startDatePeriodEndOffset: ProgramFormTab.Workflow,
  periodStartDate: ProgramFormTab.Workflow,
  periodEndDate: ProgramFormTab.Workflow,
  questionSetIds: ProgramFormTab.Questions,
  topicTemplateId: ProgramFormTab.Workflow,
  appliesTo: ProgramFormTab.Participants,
};

// The fields that decide who a review applies to. A "nobody matches" error from
// the backend stays on the form only while these are as they were.
const participantRuleKey = (program: ProgramForm) =>
  JSON.stringify(
    pick(program, [
      "ongoing",
      "appliesTo",
      "appliesToTeams",
      "appliesToManagers",
      "appliesToUsers",
      "excludedUsers",
      "excludedHireDateAfter",
    ]),
  );

const getProgramTypeFromUrlParam = (programType?: ComplianceProgramType) => {
  if (programType === ComplianceProgramType.ONE_ON_ONE) {
    return ComplianceProgramType.ONE_ON_ONE;
  }
  if (programType === ComplianceProgramType.ENGAGEMENT_SURVEY) {
    return ComplianceProgramType.ENGAGEMENT_SURVEY;
  }
  return ComplianceProgramType.REVIEW;
};

const ComplianceProgramForm = () => {
  const link = useLink();
  const location = useLocation();
  const label = useLabel();
  const { basePath, listUrl, rootCrumb, lockedProgramType, noun } =
    useProgramsSection();
  const nounLower = noun.toLowerCase();
  const isAdmin = isAdminVar();
  const organization = currentOrganizationVar();
  const currentUser = currentUserVar();

  const { complianceProgramId: complianceProgramIdParam } = useParams<{
    complianceProgramId: string;
  }>();
  const complianceProgramId = parseInt(complianceProgramIdParam);
  const isNew = isNaN(complianceProgramId);
  const isDuplicate = !isNew && location.pathname.includes("/duplicate");
  const { type: programTypeParam } = useUrlQueryParams<{
    type?: string;
  }>({
    type: undefined,
  });

  const [selectedTab, setSelectedTab] = useState(ProgramFormTab.Workflow);
  const [attemptedMode, setAttemptedMode] = useState<ProgramSaveMode | null>(
    null,
  );
  // Only the backend knows who a participant rule matches, so it is the publish
  // that finds out nobody does. Kept with the rule it was about: once the admin
  // changes the rule, the error goes until the next Publish says otherwise.
  const [noParticipantsRuleKey, setNoParticipantsRuleKey] = useState<
    string | null
  >(null);
  // The program type is locked by the section the form is opened from (Reviews
  // or Surveys), falling back to the legacy `?type=` URL param when opened from
  // the generic Programs context. Existing programs derive their type from their
  // templates once loaded (see the edit query's onCompleted below).
  const urlProgramType =
    lockedProgramType ??
    getProgramTypeFromUrlParam(programTypeParam as ComplianceProgramType);
  const [complianceProgramType, setComplianceProgramType] =
    useState<ComplianceProgramType>(urlProgramType);
  // The form as it opened: the defaults for a new review, or the review as it
  // was loaded. Save and Discard stay off until the admin changes something.
  const [untouchedComplianceProgram, setUntouchedComplianceProgram] =
    useState<ProgramForm>(() => ({
      organizationId: organization.id,
      title: "",
      ...getDefaultProgramDates(),
      ongoing: false,
      state: ComplianceProgramState.Draft,
      recurrence: ComplianceProgramRecurrence.None,
      appliesTo: isAdmin
        ? ComplianceProgramAppliesTo.Organization
        : ComplianceProgramAppliesTo.CreatorDirectReports,
      appliesToTeams: [],
      appliesToManagers: [],
      appliesToUsers: [],
      excludedUsers: [],
      scheduledReminders: [],
      evaluationPeriod: {
        type: ProgramEvaluationPeriodType.PAST_HALF_YEAR,
      },
      sharesWithSubject: true,
      // Calibration adjusts manager reviews. A survey or a 1-on-1 cycle has none,
      // and its form has no control to turn calibration off.
      calibration:
        urlProgramType === ComplianceProgramType.REVIEW
          ? ComplianceProgramCalibrationType.PostCalibration
          : ComplianceProgramCalibrationType.Off,
      steps:
        urlProgramType === ComplianceProgramType.ENGAGEMENT_SURVEY
          ? [getDefaultProgramFormStep(ComplianceProgramStep.EngagementSurvey)]
          : urlProgramType === ComplianceProgramType.ONE_ON_ONE
            ? [getDefaultProgramFormStep(ComplianceProgramStep.OneOnOne)]
            : [
                getDefaultProgramFormStep(ComplianceProgramStep.PeerReview),
                getDefaultProgramFormStep(ComplianceProgramStep.SelfReview),
                getDefaultProgramFormStep(ComplianceProgramStep.DownwardReview),
                getDefaultProgramFormStep(ComplianceProgramStep.Delivery),
              ],
    }));
  const [proposedComplianceProgram, setProposedComplianceProgram] =
    useState<ProgramForm>(untouchedComplianceProgram);
  const [availableAssessmentGroups, setAvailableAssessmentGroups] = useState<
    AssessmentGroupListFragmentFragment[]
  >([]);
  const [canUpdateTemplates, setCanUpdateTemplates] = useState(isNew);

  const homeUrl = useMemo(
    () =>
      isNew || isDuplicate ? listUrl : `${basePath}/${complianceProgramId}`,
    [complianceProgramId, isNew, isDuplicate, basePath, listUrl],
  );

  const handlePartialProgramFormUpdate = useCallback(
    (data: Partial<ProgramForm>) => {
      setProposedComplianceProgram((prev) => ({
        ...prev,
        ...data,
      }));
    },
    [],
  );

  const [
    createOrUpdateComplianceProgram,
    { loading: isSavingComplianceProgram },
  ] = useMutation<
    SaveComplianceProgramMutation,
    SaveComplianceProgramMutationVariables
  >(createOrUpdateComplianceProgramMutation);

  const { data: complianceProgramData, loading: isLoadingComplianceProgram } =
    useQuery<
      GetComplianceProgramEditQuery,
      GetComplianceProgramEditQueryVariables
    >(getComplianceProgramEditQuery, {
      variables: {
        complianceProgramId: isNew ? -1 : complianceProgramId,
        organizationId: organization.id,
        complianceProgramExists: !isNew,
      },

      onCompleted: (response) => {
        if (isNew) {
          return;
        }

        if (!response.complianceProgram) {
          link.redirect(homeUrl);
        } else {
          const hasEngagementSurveyTemplate =
            !!response.complianceProgram.engagementSurveyTemplate;
          setComplianceProgramType(
            hasEngagementSurveyTemplate
              ? ComplianceProgramType.ENGAGEMENT_SURVEY
              : ComplianceProgramType.REVIEW,
          );

          const loadedProgram = convertComplianceProgramToForm(
            organization.id,
            isDuplicate,
            response,
          );
          setProposedComplianceProgram(loadedProgram);
          setUntouchedComplianceProgram(loadedProgram);

          setCanUpdateTemplates(
            isDuplicate ||
              response.complianceProgram.state === ComplianceProgramState.Draft,
          );
        }
      },
    });

  const { loading: isLoadingAvailableAssessmentGroups } = useQuery<
    GetAssessmentGroupsListQuery,
    GetAssessmentGroupsListQueryVariables
  >(getAssessmentGroupsListQuery, {
    variables: {
      organizationId: organization.id,
      archived: canUpdateTemplates ? false : null,
    },
    onCompleted: (response) => {
      const groups = response.assessmentGroups
        ? assertEdgesNonNull(response.assessmentGroups)
        : [];
      setAvailableAssessmentGroups(groups);
    },
  });

  const scrollToFieldError = useCallback((field: string) => {
    const fieldAliases: Record<string, string> = {
      dueDate: "startDate",
      periodEndDate: "periodStartDate",
    };
    const targetField = fieldAliases[field] ?? field;
    const el = document.getElementById(`field-${targetField}`);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, []);

  const revealErrors = useCallback(
    (errors: FormError[], mode: ProgramSaveMode) => {
      setAttemptedMode(mode);
      if (errors.length === 0) {
        return;
      }
      const firstError = errors[0];
      const targetTab = fieldToTab[firstError.field];
      if (targetTab !== undefined && targetTab !== selectedTab) {
        setSelectedTab(targetTab);
      }
      window.setTimeout(() => scrollToFieldError(firstError.field), 50);
    },
    [scrollToFieldError, selectedTab],
  );

  const handleJumpToError = useCallback(
    (error: FormError) => {
      const targetTab = fieldToTab[error.field];
      if (targetTab !== undefined && targetTab !== selectedTab) {
        setSelectedTab(targetTab);
        window.setTimeout(() => scrollToFieldError(error.field), 50);
      } else {
        scrollToFieldError(error.field);
      }
    },
    [scrollToFieldError, selectedTab],
  );

  const { ConfirmationDialog: ConfirmKickoffDialog, confirm: confirmKickoff } =
    useConfirm(
      "This will kick off now",
      `The start date has already passed, so publishing sends the kickoff to everyone in this ${nounLower} straight away.`,
    );

  const noParticipantsError: FormError = useMemo(
    () => ({
      field: "appliesTo",
      message: `Nobody matches who this ${nounLower} applies to. Change it before you publish.`,
    }),
    [nounLower],
  );

  const handleSaveProgram = useCallback(
    async (state: ComplianceProgramState) => {
      const mode: ProgramSaveMode =
        state === ComplianceProgramState.Draft ? "draft" : "publish";
      const errors = validateProgramForm(proposedComplianceProgram, mode);
      if (errors.length > 0) {
        revealErrors(errors, mode);
        return;
      }
      if (
        proposedComplianceProgram.state === ComplianceProgramState.Draft &&
        state === ComplianceProgramState.Published &&
        isKickoffDatePast(proposedComplianceProgram) &&
        !(await confirmKickoff())
      ) {
        return;
      }
      createOrUpdateComplianceProgram({
        variables: {
          ...convertProgramFormToMutationVariables(
            proposedComplianceProgram,
            canUpdateTemplates,
          ),
          state,
        },
        context: {
          errorMatches: [
            {
              // Matches the backend message verbatim. Without a match the raw
              // GraphQL error is what the admin reads, and it says "Program".
              match:
                "Program participants include employees not in your management tree",
              title: `${noun} participants include employees not in your management tree`,
            },
            {
              match: "Can't update templates on a published program",
              title: `This ${nounLower} has already been published. Please refresh the page.`,
            },
            {
              match: "can't be published yet",
              title: `${noun} not published`,
              showMessage: true,
            },
            // Shown as a form error by onError below, not as a toast.
            { match: NO_PARTICIPANTS_ERROR, silent: true },
          ],
        },
        refetchQueries: [getComplianceProgramsQuery],
        // Every other error is a toast from the error link. Passing onError
        // also stops the call rejecting with nobody to catch it.
        onError: (error) => {
          if (
            error.graphQLErrors.some(({ message }) =>
              message.includes(NO_PARTICIPANTS_ERROR),
            )
          ) {
            setNoParticipantsRuleKey(
              participantRuleKey(proposedComplianceProgram),
            );
            revealErrors([noParticipantsError], mode);
          }
        },
        onCompleted: () => {
          successNotificationVar({
            title:
              state === ComplianceProgramState.Draft
                ? `${noun} saved for later`
                : proposedComplianceProgram.state ===
                    ComplianceProgramState.Draft
                  ? `${noun} published`
                  : `${noun} saved`,
          });
          link.redirect(homeUrl);
        },
      });
    },
    [
      createOrUpdateComplianceProgram,
      homeUrl,
      link,
      proposedComplianceProgram,
      canUpdateTemplates,
      revealErrors,
      confirmKickoff,
      noun,
      nounLower,
      noParticipantsError,
    ],
  );

  const handleRemoveStep = useCallback(
    (stepType: ComplianceProgramStep) => {
      const linkedSteps: Partial<
        Record<ComplianceProgramStep, ComplianceProgramStep[]>
      > = {
        [ComplianceProgramStep.SelfReview]: [
          ComplianceProgramStep.DownwardReview,
          ComplianceProgramStep.PreCalibration,
          ComplianceProgramStep.PostCalibration,
        ],
        [ComplianceProgramStep.DownwardReview]: [
          ComplianceProgramStep.SelfReview,
          ComplianceProgramStep.PreCalibration,
          ComplianceProgramStep.PostCalibration,
        ],
      };
      const reviewSteps = [
        ComplianceProgramStep.PeerReview,
        ComplianceProgramStep.UpwardReview,
        ComplianceProgramStep.DownwardReview,
      ];
      const newSteps = proposedComplianceProgram.steps.filter(
        (oldStep) =>
          oldStep.stepType !== stepType &&
          (linkedSteps[stepType] === undefined ||
            !linkedSteps[stepType].includes(oldStep.stepType)),
      );
      const remainingReviewStepCount = newSteps.filter((s) =>
        reviewSteps.includes(s.stepType),
      ).length;
      const isPostCalibration =
        !!newSteps.find(
          (s) => s.stepType === ComplianceProgramStep.DownwardReview,
        ) &&
        proposedComplianceProgram.calibration ===
          ComplianceProgramCalibrationType.PostCalibration;
      const isPreCalibration =
        !!newSteps.find(
          (s) => s.stepType === ComplianceProgramStep.DownwardReview,
        ) &&
        proposedComplianceProgram.calibration ===
          ComplianceProgramCalibrationType.PreCalibration;
      const isDelivery = !!newSteps.find(
        (s) => s.stepType === ComplianceProgramStep.Delivery,
      );

      // remove delivery step if no more review steps
      handlePartialProgramFormUpdate({
        calibration: isPreCalibration
          ? ComplianceProgramCalibrationType.PreCalibration
          : isPostCalibration
            ? ComplianceProgramCalibrationType.PostCalibration
            : ComplianceProgramCalibrationType.Off,
        sharesWithSubject: isDelivery && remainingReviewStepCount > 0,
        steps: newSteps.filter(
          (oldStep) =>
            oldStep.stepType !== ComplianceProgramStep.Delivery ||
            remainingReviewStepCount > 0,
        ),
      });
    },
    [
      handlePartialProgramFormUpdate,
      proposedComplianceProgram.steps,
      proposedComplianceProgram.calibration,
    ],
  );

  const handleAddOrUpdateStep = useCallback(
    (newStep: ProgramFormStep) => {
      const existingStep = proposedComplianceProgram.steps.find(
        (s) => s.stepType === newStep.stepType,
      );
      const additionalSteps = [];
      if (
        newStep.stepType === ComplianceProgramStep.SelfReview &&
        !proposedComplianceProgram.steps.find(
          (s) => s.stepType === ComplianceProgramStep.DownwardReview,
        )
      ) {
        additionalSteps.push(
          getDefaultProgramFormStep(ComplianceProgramStep.DownwardReview),
        );
      }
      if (
        newStep.stepType === ComplianceProgramStep.DownwardReview &&
        !proposedComplianceProgram.steps.find(
          (s) => s.stepType === ComplianceProgramStep.SelfReview,
        )
      ) {
        additionalSteps.push(
          getDefaultProgramFormStep(ComplianceProgramStep.SelfReview),
        );
      }

      // remove delivery step if no more review steps
      handlePartialProgramFormUpdate({
        steps: [
          ...proposedComplianceProgram.steps.filter(
            (oldStep) => oldStep.stepType !== newStep.stepType,
          ),
          {
            ...getDefaultProgramFormStep(newStep.stepType),
            ...(existingStep ?? {}),
            ...newStep,
          },
          ...additionalSteps,
        ],
      });
    },
    [handlePartialProgramFormUpdate, proposedComplianceProgram.steps],
  );

  const handleAssessmentGroupCreated = useCallback(
    (assessmentGroup: AssessmentGroupListFragmentFragment) => {
      setAvailableAssessmentGroups((prev) => [
        ...prev.filter(({ id }) => id !== assessmentGroup.id),
        assessmentGroup,
      ]);
    },
    [],
  );
  const handleAssessmentGroupArchived = useCallback(
    (assessmentGroupId: number) => {
      setAvailableAssessmentGroups((prev) =>
        prev.filter(({ id }) => id !== assessmentGroupId),
      );
      handlePartialProgramFormUpdate({
        steps: proposedComplianceProgram.steps.map((step) => {
          if ("questionSetIds" in step) {
            return {
              ...step,
              questionSetIds: step.questionSetIds.filter(
                (id) => id !== assessmentGroupId,
              ),
            };
          }
          return step;
        }),
      });
    },
    [handlePartialProgramFormUpdate, proposedComplianceProgram.steps],
  );

  const isLoading =
    isLoadingComplianceProgram || isLoadingAvailableAssessmentGroups;

  // Saving a draft asks for almost nothing, publishing for the full setup. The
  // errors shown are the ones for whichever button the admin last pressed.
  // Having nobody to send to blocks only Publish, and only while the rule is
  // the one the backend refused.
  const draftErrors = validateProgramForm(proposedComplianceProgram, "draft");
  const hasNoParticipants =
    noParticipantsRuleKey !== null &&
    noParticipantsRuleKey === participantRuleKey(proposedComplianceProgram);
  const publishErrors = [
    ...validateProgramForm(proposedComplianceProgram, "publish"),
    ...(hasNoParticipants ? [noParticipantsError] : []),
  ];
  const displayErrors = attemptedMode === "draft" ? draftErrors : publishErrors;
  // A copy has not been saved anywhere yet, so saving it as it is still makes
  // something. Anything else with no edits has nothing to save or discard.
  const isUnchanged =
    !isDuplicate &&
    isEqual(proposedComplianceProgram, untouchedComplianceProgram);
  const submitAttempted = attemptedMode !== null;
  // Only a draft gets the "save now, publish later" pair of buttons. Every other state
  // keeps the state it is in: publishing, resuming and reopening belong to the action
  // dropdown, and the mutation refuses any other move.
  const isDraftProgram =
    proposedComplianceProgram.state === ComplianceProgramState.Draft;
  const tabsWithErrors = useMemo(() => {
    const tabs = new Set<ProgramFormTab>();
    displayErrors.forEach((error) => {
      const tab = fieldToTab[error.field];
      if (tab !== undefined) {
        tabs.add(tab);
      }
    });
    return tabs;
  }, [displayErrors]);
  const visibleErrors = submitAttempted ? displayErrors : [];
  // A copy starts today (see convertComplianceProgramToForm), so tell the admin
  // which dates it moved from. The query still holds the source review.
  const sourceStartDate = complianceProgramData?.complianceProgram?.startDate;
  const sourceDueDate = complianceProgramData?.complianceProgram?.dueDate;
  const formatSourceDate = (date: string) =>
    moment(date).format(
      moment(date).isSame(moment(), "year") ? "D MMM" : "D MMM YYYY",
    );
  const movedDatesNotice =
    isDuplicate &&
    sourceStartDate &&
    sourceDueDate &&
    !moment(sourceStartDate).isSame(moment(), "day")
      ? `Dates moved from ${formatSourceDate(sourceStartDate)}–${formatSourceDate(
          sourceDueDate,
        )}. Check them before you publish.`
      : null;

  return (
    <Layout>
      <ConfirmKickoffDialog />
      <Layout.Header
        breadcrumbs={[
          { title: rootCrumb.title, url: rootCrumb.url },
          {
            title: `${isNew ? "New" : isDuplicate ? "Duplicate" : "Edit"} ${
              complianceProgramType === ComplianceProgramType.ENGAGEMENT_SURVEY
                ? "Survey"
                : label("review", { capitalize: true })
            }`,
            url: location.pathname,
          },
        ]}
      >
        {!isLoading && (
          <div className="flex justify-end items-center gap-2 sm:gap-4">
            <Button
              to={homeUrl}
              disabled={isSavingComplianceProgram || isUnchanged}
              text="Discard changes"
              size={ButtonSize.medium}
            />
            {isDraftProgram && (
              <Button
                type="button"
                onClick={() => handleSaveProgram(ComplianceProgramState.Draft)}
                tooltip={
                  isUnchanged
                    ? "Nothing to save yet"
                    : (draftErrors[0]?.message ?? null)
                }
                disabled={isSavingComplianceProgram || isUnchanged}
                theme={buttonTheme.lightBlue}
                size={ButtonSize.medium}
                text={isSavingComplianceProgram ? "Saving" : "Save"}
              />
            )}
            <Button
              type="button"
              onClick={() =>
                handleSaveProgram(
                  isDraftProgram
                    ? ComplianceProgramState.Published
                    : proposedComplianceProgram.state,
                )
              }
              tooltip={
                !isDraftProgram && isUnchanged
                  ? "No changes to save"
                  : (publishErrors[0]?.message ?? null)
              }
              // Publishing a draft needs no edits. Saving a published one does.
              disabled={
                isSavingComplianceProgram || (!isDraftProgram && isUnchanged)
              }
              theme={buttonTheme.primary}
              size={ButtonSize.medium}
              text={isDraftProgram ? "Publish" : "Save changes"}
            />
          </div>
        )}
      </Layout.Header>
      <Layout.Container>
        <main className="absolute inset-0">
          <div className="@container/layout-main mx-auto">
            {isLoading ? (
              <div className="mt-20 flex items-center justify-center h-full w-full">
                <Loading>Loading {nounLower}...</Loading>
              </div>
            ) : (
              <>
                <div className="z-30 px-8 pt-4 sticky top-0 bg-white">
                  <Tabs
                    options={[
                      {
                        value: ProgramFormTab.Workflow,
                        label: "Workflow",
                        hasError:
                          submitAttempted &&
                          tabsWithErrors.has(ProgramFormTab.Workflow),
                      },
                      {
                        value: ProgramFormTab.Questions,
                        label: "Questions",
                        hasError:
                          submitAttempted &&
                          tabsWithErrors.has(ProgramFormTab.Questions),
                      },
                      {
                        value: ProgramFormTab.Participants,
                        label: "Participants",
                        hasError:
                          submitAttempted &&
                          tabsWithErrors.has(ProgramFormTab.Participants),
                      },
                      {
                        value: ProgramFormTab.Notifications,
                        label: "Notifications",
                        hasError:
                          submitAttempted &&
                          tabsWithErrors.has(ProgramFormTab.Notifications),
                      },
                      // No error marker: nothing on this tab can stop a save.
                      {
                        value: ProgramFormTab.Readiness,
                        label: "Readiness",
                      },
                    ]}
                    selectedValue={selectedTab}
                    onClickOptionValue={setSelectedTab}
                  />
                </div>
                <div className="absolute inset-0 overflow-y-scroll pt-20 pb-32">
                  {/* The Workflow tab is wider than the fields in it, to leave
                      room for the Timeline beside them. Below the breakpoint the
                      Timeline stacks under the form and the fields fill the wider
                      tab, so they are not the width they were on a narrow
                      screen. */}
                  <div
                    className={`mx-auto px-8 py-6 ${
                      selectedTab === ProgramFormTab.Notifications
                        ? "max-w-(--breakpoint-2xl)"
                        : selectedTab === ProgramFormTab.Workflow
                          ? // 768 fields + 32 gap + 384 Timeline + 64 padding.
                            // Exactly the pair, so the two columns stay centred
                            // together instead of leaving a gap beside one of them.
                            "max-w-[1248px]"
                          : selectedTab === ProgramFormTab.Readiness
                            ? "max-w-(--breakpoint-lg)"
                            : "max-w-(--breakpoint-md)"
                    }`}
                  >
                    {movedDatesNotice && (
                      <Callout
                        theme="warning"
                        icon="info"
                        className="mb-6"
                        size="small"
                      >
                        {movedDatesNotice}
                      </Callout>
                    )}
                    {submitAttempted && displayErrors.length > 0 && (
                      <ProgramFormErrorBanner
                        errors={displayErrors}
                        fieldToTab={fieldToTab}
                        onJumpToError={handleJumpToError}
                      />
                    )}
                    {selectedTab === ProgramFormTab.Workflow && (
                      // Side by side once the main area is wide enough for both,
                      // stacked when it is not. A container query rather than a
                      // screen one, because collapsing the left navigation changes
                      // this width without changing the window.
                      <div className="flex flex-col gap-8 @[1200px]/layout-main:flex-row @[1200px]/layout-main:items-start">
                        <div className="min-w-0 flex-1 @[1200px]/layout-main:max-w-(--breakpoint-md)">
                          <ProgramFormWorkflow
                            complianceProgramType={complianceProgramType}
                            proposedComplianceProgram={
                              proposedComplianceProgram
                            }
                            onUpdateProposedComplianceProgram={
                              handlePartialProgramFormUpdate
                            }
                            canUpdate={canUpdateTemplates}
                            onAddOrUpdateStep={handleAddOrUpdateStep}
                            onRemoveStep={handleRemoveStep}
                            errors={visibleErrors}
                          />
                        </div>
                        {/* Sticks to the top of the form as the admin scrolls
                            through the steps, because the answer is about the step
                            they are editing.

                            `top-0`, not `top-20`: a sticky element is held inside
                            the scroll container's padding box, and this container
                            already has `pt-20` to clear the tab bar. Adding the
                            same 80px again pins it 80px lower than it sits at
                            rest, which pushes it out of line with the section
                            beside it before anybody scrolls. */}
                        <div className="@[1200px]/layout-main:sticky @[1200px]/layout-main:top-0 @[1200px]/layout-main:w-96 @[1200px]/layout-main:shrink-0">
                          <ProgramFormTimelinePreview
                            proposedComplianceProgram={
                              proposedComplianceProgram
                            }
                          />
                        </div>
                      </div>
                    )}
                    {selectedTab === ProgramFormTab.Questions && (
                      <ProgramFormQuestions
                        proposedComplianceProgram={proposedComplianceProgram}
                        canUpdate={canUpdateTemplates}
                        talentIndicatorData={
                          complianceProgramData
                            ? assertEdgesNonNull(
                                complianceProgramData?.talentIndicators,
                              )
                            : []
                        }
                        onAddOrUpdateStep={handleAddOrUpdateStep}
                        availableAssessmentGroups={availableAssessmentGroups}
                        onAssessmentGroupCreated={handleAssessmentGroupCreated}
                        onAssessmentGroupArchived={
                          handleAssessmentGroupArchived
                        }
                        errors={visibleErrors}
                      />
                    )}
                    {selectedTab === ProgramFormTab.Participants && (
                      <ProgramFormParticipants
                        creator={
                          complianceProgramData?.complianceProgram?.creator ??
                          currentUser
                        }
                        proposedComplianceProgram={proposedComplianceProgram}
                        onUpdateProposedComplianceProgram={
                          handlePartialProgramFormUpdate
                        }
                        canUpdate={canUpdateTemplates}
                        errors={visibleErrors}
                      />
                    )}
                    {selectedTab === ProgramFormTab.Notifications && (
                      <ProgramFormNotifications
                        proposedComplianceProgram={proposedComplianceProgram}
                        onUpdateProposedComplianceProgram={
                          handlePartialProgramFormUpdate
                        }
                        canUpdate={canUpdateTemplates}
                      />
                    )}
                    {selectedTab === ProgramFormTab.Readiness && (
                      <ProgramFormReadiness
                        proposedComplianceProgram={proposedComplianceProgram}
                      />
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        </main>
      </Layout.Container>
    </Layout>
  );
};

export default ComplianceProgramForm;
